import React, { useState } from 'react';
import quizData from '../jsons/Multiple_choices.json';
import TestQuestion from './TestQuestion';
import TestResults from './TestResults';

const LevelTest = () => {
  const [testState, setTestState] = useState('start'); // 'start', 'testing', 'results'
  const [selectedQuestions, setSelectedQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [chapterScores, setChapterScores] = useState({});
  const [totalScore, setTotalScore] = useState(0);

  // Randomly select 3 questions from each chapter
  const selectRandomQuestions = () => {
    const chapters = Object.keys(quizData);
    const selected = [];
    const scores = {};

    chapters.forEach(chapter => {
      const chapterQuestions = quizData[chapter];
      if (chapterQuestions && chapterQuestions.length > 0) {
        // Get random indices for 3 questions (or all if less than 3)
        const questionsToSelect = Math.min(2, chapterQuestions.length);
        const randomIndices = [];
        
        while (randomIndices.length < questionsToSelect) {
          const randomIndex = Math.floor(Math.random() * chapterQuestions.length);
          if (!randomIndices.includes(randomIndex)) {
            randomIndices.push(randomIndex);
          }
        }

        // Add selected questions with chapter info
        randomIndices.forEach(index => {
          selected.push({
            ...chapterQuestions[index],
            chapter: chapter,
            originalIndex: index
          });
        });

        // Initialize chapter scores
        scores[chapter] = {
          correct: 0,
          total: questionsToSelect
        };
      }
    });

    return { questions: selected, scores };
  };

  const startTest = () => {
    const { questions, scores } = selectRandomQuestions();
    setSelectedQuestions(questions);
    setChapterScores(scores);
    setCurrentQuestionIndex(0);
    setAnswers({});
    setTotalScore(0);
    setTestState('testing');
  };

  const handleAnswerSubmit = (answer) => {
    const currentQuestion = selectedQuestions[currentQuestionIndex];
    const isCorrect = answer === currentQuestion.right_answer;
    
    // Update answers
    const newAnswers = {
      ...answers,
      [currentQuestionIndex]: {
        selected: answer,
        correct: isCorrect,
        question: currentQuestion
      }
    };
    setAnswers(newAnswers);

    // Update scores
    if (isCorrect) {
      setTotalScore(prev => prev + 1);
      setChapterScores(prev => ({
        ...prev,
        [currentQuestion.chapter]: {
          ...prev[currentQuestion.chapter],
          correct: prev[currentQuestion.chapter].correct + 1
        }
      }));
    }
  };

  const nextQuestion = () => {
    if (currentQuestionIndex < selectedQuestions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    } else {
      // Test completed
      setTestState('results');
    }
  };

  const previousQuestion = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  };

  const retakeTest = () => {
    setTestState('start');
    setSelectedQuestions([]);
    setCurrentQuestionIndex(0);
    setAnswers({});
    setChapterScores({});
    setTotalScore(0);
  };

  const backToMenu = () => {
    setTestState('start');
  };

  const currentQuestion = selectedQuestions[currentQuestionIndex];
  const isCurrentQuestionAnswered = answers[currentQuestionIndex] !== undefined;
  const canProceed = isCurrentQuestionAnswered;

  // Start screen
  if (testState === 'start') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 flex items-center justify-center p-6">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-2xl w-full">
          <div className="text-center">
            <h1 className="text-3xl font-bold text-gray-800 mb-4">
              Calculus Level Test
            </h1>
            <div className="text-gray-600 mb-8 space-y-3">
              <p className="text-lg">
                သင်၏ Calculus အဆင့်ကို စစ်ဆေးရန် အတွက် ဒီ test ကို လုပ်ကြည့်ပါ။
              </p>
              <div className="bg-blue-50 p-4 rounded-lg text-left">
                <h3 className="font-semibold text-blue-800 mb-2">Test အကြောင်း:</h3>
                <ul className="text-blue-700 space-y-1 text-sm">
                  <li>• အခန်းတစ်ခုချင်းစီမှ မေးခွန်း ၂ ခုစီ ကျပန်း ရွေးချယ်မည်။</li>
                  <li>• မေးခွန်း တစ်ခုလျှင် အကြိမ်တစ်ကြိမ်သာ ဖြေဆိုခွင့်ရမည်။</li>
                  <li>• Hint များ မပါရှိပါ။</li>
                  <li>• ရမှတ်အရ လိုအပ်သည့် အခန်းများကို လေ့လာရန် အကြံပြုချက်များ ပေးပါမည်။</li>
                </ul>
              </div>
            </div>
            <button
              onClick={startTest}
              className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-8 rounded-lg transition-colors text-lg"
            >
              Test စတင်မည်
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Results screen
  if (testState === 'results') {
    return (
      <TestResults
        chapterScores={chapterScores}
        totalScore={totalScore}
        totalQuestions={selectedQuestions.length}
        onRetakeTest={retakeTest}
        onBackToMenu={backToMenu}
      />
    );
  }

  // Testing screen
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-800">Level Test</h1>
              <p className="text-gray-600">
                မေးခွန်း {currentQuestionIndex + 1} / {selectedQuestions.length}
              </p>
            </div>
            <div className="text-right">
              <p className="text-sm text-gray-600">Chapter</p>
              <p className="font-medium text-gray-800">{currentQuestion?.chapter}</p>
            </div>
          </div>
          
          {/* Progress Bar */}
          <div className="mt-4">
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                style={{
                  width: `${((currentQuestionIndex + (isCurrentQuestionAnswered ? 1 : 0)) / selectedQuestions.length) * 100}%`
                }}
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">
              ပြီးသွားသော မေးခွန်း: {Object.keys(answers).length} / {selectedQuestions.length}
            </p>
          </div>
        </div>

        {/* Question */}
        {currentQuestion && (
          <TestQuestion
            question={currentQuestion}
            questionNumber={currentQuestionIndex + 1}
            onAnswerSubmit={handleAnswerSubmit}
            isAnswered={isCurrentQuestionAnswered}
          />
        )}

        {/* Navigation */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex justify-between items-center">
            <button
              onClick={previousQuestion}
              disabled={currentQuestionIndex === 0}
              className={`px-6 py-2 rounded-lg font-medium transition-colors ${
                currentQuestionIndex === 0
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
              }`}
            >
              ← ယခင်မေးခွန်း
            </button>

            <div className="text-center">
              <p className="text-sm text-gray-600">
                လက်ရှိ ရမှတ်: <span className="font-bold text-blue-600">{totalScore}</span>
              </p>
            </div>

            {currentQuestionIndex === selectedQuestions.length - 1 ? (
              <button
                onClick={nextQuestion}
                disabled={!canProceed}
                className={`px-6 py-2 rounded-lg font-medium transition-colors ${
                  !canProceed
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-green-600 hover:bg-green-700 text-white'
                }`}
              >
                ရလဒ် ကြည့်မည်
              </button>
            ) : (
              <button
                onClick={nextQuestion}
                disabled={!canProceed}
                className={`px-6 py-2 rounded-lg font-medium transition-colors ${
                  !canProceed
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-blue-600 hover:bg-blue-700 text-white'
                }`}
              >
                နောက်မေးခွန်း →
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LevelTest;
